import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  LinearProgress,
  MenuItem,
  Pagination,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import PlayCircleOutlineRoundedIcon from '@mui/icons-material/PlayCircleOutlineRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import { listRecordings } from '../api/recordings';

const formatDateTime = (value) => {
  if (!value) return '-';
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return '-';
  return dt.toLocaleString();
};

const RecordingsPage = ({
  LayoutComponent,
  title = 'Recordings',
  description = 'Completed sessions and their recordings.',
}) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [subjectFilter, setSubjectFilter] = useState('');

  const pages = useMemo(() => Math.max(1, Math.ceil((total || 0) / pageSize)), [total, pageSize]);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { page, pageSize };
      if (query) params.q = query;
      if (subjectFilter) params.subject = subjectFilter;
      const data = await listRecordings(params);
      setItems(Array.isArray(data?.items) ? data.items : []);
      setTotal(Number.isFinite(data?.total) ? data.total : 0);
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Failed to load recordings');
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [page, pageSize, query, subjectFilter]);

  const subjects = useMemo(() => {
    const vals = items.map((item) => item.subject).filter(Boolean);
    return Array.from(new Set(vals)).sort();
  }, [items]);

  const groupedItems = useMemo(() => {
    if (!items.length) return {};
    return items.reduce((acc, item) => {
      const key = item.subject || 'Other';
      if (!acc[key]) acc[key] = [];
      acc[key].push(item);
      return acc;
    }, {});
  }, [items]);

  return (
    <LayoutComponent>
      <Box>
        {loading && <LinearProgress />}
        {error && <Alert severity="error">{error}</Alert>}

        <Card elevation={1} sx={{ borderRadius: 3 }}>
          <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
            <Stack spacing={3}>
              <Stack spacing={0.5}>
                <Typography variant="h6" fontWeight={700}>
                  {title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {description}
                </Typography>
              </Stack>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  label="Search by title"
                  value={query}
                  onChange={(e) => {
                    setPage(1);
                    setQuery(e.target.value);
                  }}
                  InputProps={{
                    startAdornment: <SearchRoundedIcon color="action" sx={{ mr: 1 }} />,
                  }}
                />
                <TextField
                  select
                  label="Filter by subject"
                  value={subjectFilter}
                  onChange={(e) => {
                    setPage(1);
                    setSubjectFilter(e.target.value);
                  }}
                  sx={{ minWidth: 200 }}
                >
                  <MenuItem value="">All subjects</MenuItem>
                  {subjects.map((subject) => (
                    <MenuItem key={subject} value={subject}>
                      {subject}
                    </MenuItem>
                  ))}
                </TextField>
                <Button
                  startIcon={<RefreshRoundedIcon />}
                  onClick={() => {
                    setQuery('');
                    setSubjectFilter('');
                    setPage(1);
                    load();
                  }}
                >
                  Refresh
                </Button>
              </Stack>

              {items.length === 0 && !loading ? (
                <Alert severity="info">No recordings yet.</Alert>
              ) : (
                <Stack spacing={3}>
                  {Object.entries(groupedItems).map(([subject, rows]) => (
                    <Card key={subject} variant="outlined">
                      <CardHeader title={subject} />
                      <CardContent>
                        <TableContainer>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Session</TableCell>
                                <TableCell>Educator</TableCell>
                                <TableCell>Start</TableCell>
                                <TableCell>Recording</TableCell>
                                <TableCell>Chapters</TableCell>
                                <TableCell align="right">Action</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {rows.map((item) => (
                                <TableRow key={item._id || item.title}>
                                  <TableCell>
                                    <Stack spacing={0.5}>
                                      <Typography fontWeight={700}>{item.title || item.subject || 'Session'}</Typography>
                                      <Typography variant="body2" color="text.secondary">
                                        {item.subject}
                                      </Typography>
                                    </Stack>
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="body2">
                                      {item.educator
                                        ? `${item.educator.name || ''} ${item.educator.surname || ''}`.trim()
                                        : '-'}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="body2">{formatDateTime(item.startTime)}</Typography>
                                    {item.durationMinutes ? (
                                      <Chip size="small" label={`${item.durationMinutes} min avg`} />
                                    ) : null}
                                  </TableCell>
                                  <TableCell>
                                    {item.recordingUrl ? (
                                      <Chip color="success" size="small" label="Available" />
                                    ) : (
                                      <Chip color="warning" size="small" label="Pending" />
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    {(item.recordingChapters || []).length ? (
                                      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                                        {item.recordingChapters.map((c, idx) => (
                                          <Button
                                            key={`${item._id}-c-${idx}`}
                                            size="small"
                                            variant="outlined"
                                            disabled={!item.recordingUrl}
                                            onClick={() => {
                                              if (!item.recordingUrl) return;
                                              window.open(
                                                `${item.recordingUrl}${item.recordingUrl.includes('?') ? '&' : '?'}t=${Math.max(0, Math.floor(c.startSeconds))}`,
                                                '_blank',
                                                'noopener,noreferrer'
                                              );
                                            }}
                                          >
                                            {c.label}
                                          </Button>
                                        ))}
                                      </Stack>
                                    ) : (
                                      <Typography variant="caption" color="text.secondary">
                                        -
                                      </Typography>
                                    )}
                                  </TableCell>
                                  <TableCell align="right">
                                    <Button
                                      variant="contained"
                                      size="small"
                                      startIcon={<PlayCircleOutlineRoundedIcon />}
                                      disabled={!item.recordingUrl}
                                      onClick={() =>
                                        item.recordingUrl &&
                                        window.open(item.recordingUrl, '_blank', 'noopener,noreferrer')
                                      }
                                    >
                                      Watch
                                    </Button>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </CardContent>
                    </Card>
                  ))}
                </Stack>
              )}

              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={2}
                alignItems={{ xs: 'flex-start', md: 'center' }}
                justifyContent="space-between"
              >
                <Typography variant="body2" color="text.secondary">
                  Showing {items.length} of {total}
                </Typography>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Pagination
                    count={pages}
                    page={Math.min(page, pages)}
                    onChange={(_, value) => setPage(value)}
                    color="primary"
                    shape="rounded"
                    size="small"
                  />
                  <TextField
                    select
                    label="Rows per page"
                    value={pageSize}
                    onChange={(event) => {
                      setPage(1);
                      setPageSize(parseInt(event.target.value, 10));
                    }}
                    size="small"
                    sx={{ minWidth: 120 }}
                  >
                    {[10, 20, 50, 100].map((n) => (
                      <MenuItem key={n} value={n}>
                        {n} / page
                      </MenuItem>
                    ))}
                  </TextField>
                </Stack>
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      </Box>
    </LayoutComponent>
  );
};

export default RecordingsPage;
